import diffApplier from './diff/object-applier';
import StorageManager from './storage/storage-manager';
import diffMethods from './diff/methods';
import NetworkProvider from './network-provider';
import loggerFactory from '../logger';
import { subscribable, x } from '../utils';
import ExecutionEngine from '../execution-engine';

const log = loggerFactory.get('repository');
const DEFAULT_ID = 'id';

/**
 * ## Repository
 * Repository is a centralized module to facilitate data flows in the client.
 *
 * Features:
 * * Synchronizing data with network layer (fetch and subscriptions)
 * * Caching data
 * * Sequential ordering, job queries are queued and executed in order.
 *
 * ### Configuration
 *
 * This module enables configuration as following:
 *
 * | Parameter         | Type   | Default value  | Description |
 * | ---               | ---    | ---            | --- |
 * | maxSize           | Number | 0              | The maximum size of the data storage per collection. 0 means that there are no limit. |
 * | maxRepositorySize | Number | 0              | The maximum size of the data storage for all collections. 0 means that there are no limit. |
 * | executionEngine   | ExecutionEngine | new ExecutionEngine() | Queue for sending requests via web socket. Used for communication between SocketEnablerClient and Repository. |
 *
 * ### Examples
 *
 * #### Interface
 *
 *    import { Repository } from '@pbs/ngen-assets';
 *
 * @module module:modules/repository
 *
 * @public
 */
export default class Repository {
    constructor() {
        this.idKeyCollection = {};
        this.network = undefined;
        this.storageManager = undefined;
        this.channel = undefined;
        this.repositoryConfig = undefined;
        this.localeCollection = {};
        this.executionEngine = undefined;
        this.wasInitialized = false;
    }

    /**
     * Initialize repository.
     *
     * This method is called by the frameworks life-cycle manager.
     * Normally, you should not have to call this method yourself.
     *
     * @example
     * repository.init();
     *
     * @param {Object} config Configuration properties. Optional.
     *
     * @return {Object} Repository instance with applied configuration
     *
     * @memberof module:modules/repository
     *
     * @function init
     *
     * @instance
     *
     * @private
     */
    init(config) {
        this.repositoryConfig = config = config || { maxRepositorySize: 0, maxSize: 0 };
        this.executionEngine = config.executionEngine || new ExecutionEngine();
        this.idKeyCollection = {};
        this.localeCollection = {};
        this.channel = subscribable.mixin({});
        this.storageManager = new StorageManager(x.pick(config, 'maxRepositorySize'));
        this.network = new NetworkProvider(config);
        this.wasInitialized = true;
        return this;
    }

    /**
     * Returns the max number of concurrent requests.
     * I.e. it is the total number of open requests for all endpoints.
     *
     * @example
     * const maxConcurrentRequests = repository.getMaxConcurrentRequests();
     *
     * @memberof module:modules/repository
     *
     * @function getMaxConcurrentRequests
     *
     * @returns {number} Maximum number of concurrent requests
     *
     * @instance
     *
     * @public
     */
    getMaxConcurrentRequests() {
        this.assertInitialized();
        return this.executionEngine.getMaxConcurrentRequests();
    }

    /**
     * A global setting that will restrict the number of ongoing/concurrent requests.
     * I.e. it is the total number of open requests for all endpoints.
     *
     * @example
     * // Maximum three concurrent request
     * repository.setMaxConcurrentRequests(3);
     *
     * @param {Number} max Maximum number of concurrent request
     *
     * @memberof module:modules/repository
     *
     * @function setMaxConcurrentRequests
     *
     * @instance
     *
     * @public
     */
    setMaxConcurrentRequests(max) {
        this.assertInitialized();
        this.executionEngine.setMaxConcurrentRequests(max);
    }

    /**
     * Registers and configures a collection.
     * A collection needs to be registered before it is available to use in the repository.
     *
     * @example
     * //Configuring a collection that is synchronized with socket-enabler
     * repository.addCollection('my-collection', {
     *      networkProvider: socketEnabler.createNetworkProvider("my-endpoint")
     * });
     *
     * @param {String} collection Collection key
     * @param {Object} config Configuration properties. Optional
     *
     * @property {Object} config.networkProvider Repository will use network provider to synchronize data and keep it up to date.
     * @property {Number} config.maxSize Maximum number of entities to store in collection. Optional - defaults to unlimited.
     * @property {String} config.idKey Set the name of the id property for entities. Optional - defaults to 'id'
     * @property {String} config.locale Sets the locale that the collection is configured against, required for diffs on LocalizedString. Optional - defaults to ''
     *
     * @memberof module:modules/repository
     *
     * @function addCollection
     *
     * @instance
     *
     * @public
     */
    addCollection(collection, config) {
        this.assertInitialized();
        this.config = config || {};
        if (this.config.idKey) {
            this.idKeyCollection[collection] = this.config.idKey;
        }

        if (this.config.locale) {
            this.localeCollection[collection] = this.config.locale;
        }
        const storageConfig = x.pick(this.config, 'maxSize');
        x.defaults(storageConfig, {
            maxSize: this.repositoryConfig.maxSize,
        });
        storageConfig.restorable = !!this.config.networkProvider;

        storageConfig.itemIsRemovable = (coll, identifier) =>
            this.network.hasProvider(coll) &&
            !this.network.hasNetworkSubscription(coll, identifier);

        this.storageManager.addStorage(collection, storageConfig);

        if (this.config.networkProvider) {
            this.network.add(collection, this.config.networkProvider);
        }
    }

    /**
     * Retrieve an instrumentation object for debugging.
     *
     * @return {Object} Instrumentation object
     *
     * @private
     */
    instrumentation() {
        return {
            __name__: 'Repository',
            __this__: true,
            __description__: 'A central data storage module',
            network: this.network,
            channel: this.channel,
            executionEngine: this.executionEngine,
            storageManager: this.storageManager,
            idKeys: this.idKeyCollection,
            diffMethods: diffMethods, // Ugly way to leak the abstraction for repositoryLinker
        };
    }

    /**
     * Get data entity. Repository will fetch from network provider (and save result)
     * if entity is not already stored.
     *
     * @example
     * repository.get('my-domain', 123)
     *     .then(function(data){...}) // work with data
     *     .catch(function(err){...}) // handle error
     *
     * @param {String} collection Collection entity belongs to
     * @param {String} identifier Identifier of entity
     *
     * @return {Promise} result of operation
     *
     * @memberof module:modules/repository
     *
     * @function get
     *
     * @instance
     *
     * @public
     */
    get(collection, identifier) {
        this.assertInitialized();

        const msg = this.getValidationMessage(
            { name: 'collection', isOk: collection, value: collection },
            {
                name: 'identifier',
                isOk: identifier !== undefined && identifier !== null,
                value: identifier,
            }
        );
        if (msg) {
            return Promise.reject(msg);
        }

        const doFetch = originalError => {
            return new Promise((fetchResolve, fetchReject) => {
                this.executionEngine.addJob(collection, identifier, {
                    job: done => {
                        const onComplete = (err, data) => {
                            err ? fetchReject(err) : fetchResolve(data);
                            x.async(done)();
                        };

                        if (this.network.hasProvider(collection)) {
                            this.doNetworkFetchAndSave(collection, identifier)
                                .then(data => onComplete(null, data))
                                .catch(err => onComplete(err));
                        } else {
                            // The original error is more meaningful in this case
                            onComplete(originalError);
                        }
                    },
                    info: 'GET',
                });
            });
        };

        return this.storageManager.getById(collection, identifier).catch(doFetch);
    }

    /**
     * Query an entity for specific data.
     *
     * @example <caption>Data entity stored in 'my-collection', 'my-entity'</caption>
     * const entity = {
     *     id: 'my-entity',
     *     foo: 'bar',
     *     items: [
     *         {
     *             id: '1',
     * 	           name: 'one'
     *         }, {
     *     		   id: '2',
     *     		   name: 'two'
     *         }
     *     ]
     * };
     *
     * @example <caption>Query the data for 'foo'</caption>
     * repository.query('my-collection', 'my-entity', 'foo')
     *     .then(function(data){...}) // work with data
     *     .catch(function(err){...}) // handle error
     *
     * @example <caption>Query the data for first item in list 'items'</caption>
     * repository.query('my-collection', 'my-entity', 'items#1')
     *     .then(function(data){...}) // work with data
     *     .catch(function(err){...}) // handle error
     *
     * @example <caption>Query the data for 'name' in the second item in list 'items'</caption>
     * repository.query('my-collection', 'my-entity', 'items#2.name')
     *     .then(function(data){...}) // work with data
     *     .catch(function(err){...}) // handle error
     *
     * @param {String} collection Collection entity belongs to
     * @param {String} identifier Identifier of entity
     * @param {String} query Query string
     *
     * @return {Promise} result of operation
     *
     * @memberof module:modules/repository
     *
     * @function query
     *
     * @instance
     *
     * @public
     */
    query(collection, identifier, query) {
        this.assertInitialized();
        const msg = this.getValidationMessage(
            { name: 'collection', isOk: collection, value: collection },
            {
                name: 'identifier',
                isOk: identifier !== undefined && identifier !== null,
                value: identifier,
            }
        );
        if (msg) {
            return Promise.reject(msg);
        }

        return this.storageManager
            .getById(collection, identifier)
            .then(data => diffMethods.retriever(data, query, this.getIdKey(collection))());
    }

    /**
     * Get data entity by forcing a fetch from network provider and ignoring locally stored data.
     *
     * @example
     * repository.fetch('my-domain', 'my-identifier')
     *     .then(function(data){...}) // work with data
     *     .catch(function(err){...}) // handle error
     *
     * @param {String} collection Collection entity belongs to
     * @param {String} identifier Identifier of entity
     *
     * @return {Promise} result of operation
     *
     * @memberof module:modules/repository
     *
     * @function fetch
     *
     * @instance
     *
     * @public
     */
    fetch(collection, identifier) {
        this.assertInitialized();

        return new Promise((resolve, reject) => {
            this.validate(
                { name: 'collection', isOk: collection, value: collection },
                {
                    name: 'identifier',
                    isOk: identifier !== undefined && identifier !== null,
                    value: identifier,
                }
            );

            this.executionEngine.addJob(collection, identifier, {
                job: done => {
                    const onComplete = (err, data) => {
                        err ? reject(err) : resolve(data);
                        x.async(done)();
                    };

                    this.doNetworkFetchAndSave(collection, identifier)
                        .then(data => onComplete(null, data))
                        .catch(err => onComplete(err));
                },
                info: 'FETCH',
            });
        });
    }

    /**
     * Save data entity. Result of operation will be provided as {Promise}
     * and published on subscription channel. Data entity must contain a valid identifier
     * property at root level.
     *
     * @example
     * const entity = {
     *     id: 123,
     *     versionTag: 1,
     *     test: 'hello'
     * };
     *
     * repository.save('my-domain', entity)
     *     .then(function(data){...}) // work with data
     *     .catch(function(err){...}) // handle error
     *
     * @param {String} collection Collection entity belongs to
     * @param {Object} data Data entity to save
     * @param {boolean} [skipPublish = false] True if internal channel publish should be skipped
     *
     * @return {Promise} result of operation
     *
     * @memberof module:modules/repository
     *
     * @function save
     *
     * @instance
     *
     * @public
     */
    save(collection, data, skipPublish = false) {
        this.assertInitialized();

        // Wrapping into promise instead of promise chaining since getId might throw exception.
        // In this case, save will not return rejected promise, what is wrong
        return new Promise((resolve, reject) => {
            this.validate({ name: 'collection', isOk: collection, value: collection });

            const id = this.getId(collection, data);
            this.storageManager
                .save(collection, id, data)
                .then(data2 => {
                    !skipPublish &&
                        this.channel.publish(`${collection}_${id}`, {
                            dataProvider: data2 && this.cloneProvider(data2, collection, id),
                            changeset: null,
                        });
                    resolve(data2);
                })
                .catch(err => reject(err));
        });
    }

    /**
     * Save unavailable data entity. Result of operation will be provided as {Promise}
     * and published on subscription channel.
     *
     * @param {String} collection Collection entity belongs to
     * @param {string} itemId Entry id to save
     * @param {boolean} [skipPublish = false] True if internal channel publish should be skipped
     *
     * @return {Promise} result of operation
     *
     * @memberof module:modules/repository
     *
     * @function saveUnavailable
     *
     * @instance
     *
     * @public
     */
    saveUnavailable(collection, itemId, skipPublish = false) {
        return new Promise((resolve, reject) => {
            this.validate({ name: 'collection', isOk: collection, value: collection });
            this.storageManager
                .save(collection, itemId, null)
                .then(data => {
                    !skipPublish &&
                        this.channel.publish(`${collection}_${itemId}`, {
                            dataProvider: () => Promise.resolve(null),
                            changeset: null,
                        });
                    resolve(data);
                })
                .catch(err => reject(err));
        });
    }

    /**
     * Apply and save a diff on a data entity. Result of operation and the full data entity (after diff
     * has been applied) will be provided as {Promise} and published on the subscription channel.
     *
     * A versionTag property must be provided in the data root. The new version tag is compared to the
     * version tag already stored, if the new versionTag is not in the next sequential order the
     * repository treats this as data being out of sync and will fetch the full entity from network
     * provider (if configured) to synchronize state.
     *
     * @example <caption>Simple diff format</caption>
     * const simpleDiff = {
     *     versionTag: 2,
     *     entityId: 123,
     *     changeset: [{type: 'changed', key: 'foo', value: 'bar'}]
     * }
     *
     * @example <caption>Compound diff format</caption>
     * const savedEntity = {
     *     id: 123,
     *     versionTag: 1,
     *     name: 'initial',
     *     items: [{
     *         id: 123,
     *         foo: 'old'
     *     }]
     * };
     *
     * @example <caption>Complex diff format</caption>
     * const diff = {
     *     versionTag: 2,
     *     entityId: 123,
     *     changeset: [
     *         {type: 'changed', key: 'name', value: 'New name'},
     *         {type: 'removed', key: 'items#123'},
     *         {type: 'added', key: 'items', value: {id: 321, foo: 'bar'}}
     *     ]
     * };
     *
     * @example <caption>Saving the diff</caption>
     * repository.saveDiff('my-domain', diff)
     *     .then(function(data){...}) // work with data
     *     .catch(function(err){...}) // handle error
     *
     *      // data == {
     *      //     id: 123,
     *      //     versionTag: 1,
     *      //     name: 'New name',
     *      //     items: [{
     *      //         id: 321,
     *      //         foo: 'bar'
     *      //     }]
     *      // };
     *  });
     *
     * @param {String} collection Collection entity belongs to
     * @param {Object} diff Diff to apply and save
     * @param {boolean} [skipPublish = false] True if internal channel publish should be skipped
     *
     * @return {Promise} result of operation
     *
     * @memberof module:modules/repository
     *
     * @function saveDiff
     *
     * @instance
     *
     * @private
     */
    saveDiff(collection, diff, skipPublish = false) {
        this.assertInitialized();

        const msg = this.getValidationMessage(
            { name: 'collection', isOk: collection, value: collection },
            { name: 'diff', isOk: diff, value: diff }
        );
        if (msg) {
            return Promise.reject(msg);
        }

        let isDiffOk = false;

        const applyDiff = data => {
            if (data && diff.versionTag - 1 === data.versionTag) {
                const applier = diffApplier.createApplier(data, {
                    idKey: this.getIdKey(collection),
                    locale: this.getLocale(collection),
                });
                diff.changeset.forEach(change => applier(change));
                data.versionTag = diff.versionTag;
                isDiffOk = true;
                return data;
            }
        };

        const publish = data => {
            const id = this.getId(collection, data);
            !skipPublish &&
                this.channel.publish(`${collection}_${id}`, {
                    dataProvider: data && this.cloneProvider(data, collection, id),
                    changeset: (isDiffOk && diff.changeset) || null,
                });
            return data;
        };

        const fetchIfNeeded = data =>
            isDiffOk ? Promise.resolve(data) : this.doNetworkFetch(collection, diff.entityId);

        return this.storageManager
            .getById(collection, diff.entityId)
            .catch(() => null) // Ignore error and re-fetch
            .then(applyDiff)
            .then(fetchIfNeeded)
            .then(data => this.storageManager.save(collection, this.getId(collection, data), data))
            .then(publish);
    }

    /**
     * Remove a data entity from the storage, and publish the removal on the subscription channel.
     *
     * @example
     * repository.remove('domain', '123456')
     *
     * @param {String} collection Collection entity belongs to
     * @param {String} identifier Identifier of entity
     * @param {boolean} [skipPublish = false] True if internal channel publish should be skipped
     *
     * @return {Promise} result of operation
     *
     * @memberof module:modules/repository
     *
     * @function remove
     *
     * @instance
     *
     * @public
     */
    remove(collection, identifier, skipPublish = false) {
        this.assertInitialized();

        return this.storageManager.remove(collection, identifier).then(data => {
            !skipPublish &&
                this.channel.publish(`${collection}_${identifier}`, {
                    dataProvider: null,
                    changeset: null,
                });
            return Promise.resolve(data);
        });
    }

    /**
     * Subscribe to updates on a data entity. Repository will subscribe to network provider (if configured) to keep
     * the entity up to date, only one network subscription will be kept active against a network provider
     * per collection. Promise for subscription status is a part of return value.
     *
     * Callback is triggered when data entity changes, either via network provider or through local save, saveDiff or remove.
     * Initial entity state is provided on promise resolve (note: callback is not invoked at that time).
     *
     * All errors to be signalled with rejected promise
     *
     * @example
     * const subscription = repository.subscribe('my-domain', 'my-identifier', function (data) {});
     * subscription.promise.then(function (data) {}) // use initial entity state if needed
     *    .catch(function (err) {})
     * ...
     * subscription.dispose()
     *
     * @param {String} collection Collection entity belongs to
     * @param {String} identifier Identifier of entity
     * @param {Function} callback Callback handler for updates ``function (data) {}``. An update where data
     * is null indicates that the entity has been removed from the repository.
     *
     * @return {Object} ``{ promise: promise, dispose: function() {} }``
     *   Placeholder containing:
     *   promise to be fulfilled on subscription complete,
     *   function, network subscription will be disposed if last subscription is disposed.
     *
     * @memberof module:modules/repository
     *
     * @function subscribe
     *
     * @instance
     *
     * @public
     */
    subscribe(collection, identifier, callback) {
        this.assertInitialized();

        let internalSubscription;
        const self = this;

        const promise = new Promise((resolve, reject) => {
            this.validate(
                { name: 'callback', isOk: callback, value: callback },
                { name: 'collection', isOk: collection, value: collection },
                {
                    name: 'identifier',
                    isOk: identifier !== undefined && identifier !== null,
                    value: identifier,
                }
            );

            const subscriptionStatus = {
                ready: false,
                lastData: null,
            };

            // Subscribe to internal channel for updates on entity
            // Do not invoke callback until the subscription is ready
            const escapedName = escapeRegExp(`${collection}_${identifier}`);
            internalSubscription = this.channel.subscribe(`^${escapedName}$`, message => {
                if (subscriptionStatus.ready) {
                    if (message.dataProvider) {
                        message
                            .dataProvider()
                            .then(data => callback(data, message.changeset))
                            .catch(err => log.warn(`Callback was not invoked (${err})`));
                    } else {
                        callback(null, message.changeset);
                    }
                } else {
                    subscriptionStatus.lastData = message.dataProvider;
                }
            });

            if (!this.network.hasProvider(collection)) {
                resolveData(false);
            } else if (this.network.hasNetworkSubscription(collection, identifier)) {
                // If network connection exists, data is already fetched or fetching is in progress
                resolveData(true);
            } else {
                addJob();
            }

            function resolveData(subscriptionExists) {
                if (subscriptionStatus.lastData) {
                    const finish = () => {
                        if (subscriptionExists) {
                            self.network.increaseNetworkSubscription(collection, identifier);
                        }
                        subscriptionStatus.ready = true;
                    };

                    subscriptionStatus
                        .lastData()
                        .then(data => {
                            resolve(data);
                            finish();
                        })
                        .catch(err => {
                            log.error(
                                `Repository subscribe promise was resolved to null due to error ${err}`
                            );
                            resolve(null);
                            finish();
                        });
                } else {
                    self.storageManager
                        .getById(collection, identifier)
                        .then(data => {
                            resolve(data);
                            if (subscriptionExists) {
                                self.network.increaseNetworkSubscription(collection, identifier);
                            }
                            subscriptionStatus.ready = true;
                        })
                        .catch(() => {
                            if (subscriptionExists) {
                                // It is for the corner case when subscription already existed,
                                // but storage does not contain the data (probably waiting for network operation to complete).
                                // In this case, give up and put a task into a queue.
                                addJob();
                                // It might happen that lastData just became available, then use it.
                            } else if (subscriptionStatus.lastData) {
                                subscriptionStatus
                                    .lastData()
                                    .then(data => {
                                        resolve(data);
                                        subscriptionStatus.ready = true;
                                    })
                                    .catch(err => {
                                        log.warn(
                                            `Repository subscribe promise was resolved to null due to error ${err}`
                                        );
                                        resolve(null);
                                        subscriptionStatus.ready = true;
                                    });
                            } else {
                                resolve(null);
                                subscriptionStatus.ready = true;
                            }
                        });
                }
            }

            function job(done) {
                if (self.network.hasNetworkSubscription(collection, identifier)) {
                    self.network.increaseNetworkSubscription(collection, identifier);
                    onComplete();
                    return;
                }

                const provideVersionTag = cb => {
                    self.storageManager
                        .getById(collection, identifier)
                        .then(data => cb(data && data.versionTag))
                        .catch(() => cb());
                };

                self.network.subscribe(
                    collection,
                    identifier,
                    provideVersionTag,
                    onSubscribe,
                    onUpdate
                );

                function onSubscribe(err, data) {
                    if (err) {
                        onComplete(err);
                    } else if (data) {
                        onUpdate(data, null)
                            .then(() => onComplete())
                            .catch(onComplete);
                    } else {
                        // If data is undefined that means data is UP_TO_DATE and we should not trigger any updates.
                        // It might happen for socket enabler client
                        onComplete();
                    }
                }

                function onUpdate(data, diff) {
                    if (diff) {
                        return self.saveDiff(collection, diff);
                    } else if (data) {
                        return self.save(collection, data);
                    }
                    // Neither data nor diff, it means that the item has been removed.
                    return self.remove(collection, identifier).then(() => null);
                }

                function onComplete(err) {
                    if (err) {
                        // if err is NOT_AVAILABLE then subscription is up, but id was not found. updates will come when id exists
                        if (isUnavailable(err)) {
                            resolve(null);
                        } else {
                            reject(err);
                        }
                    } else {
                        resolveData(false);
                    }

                    subscriptionStatus.ready = true;
                    x.async(done)(); // Notify the queue in the next frame, in order to let promise.then handler to run before launch of the next job
                }
            }

            function addJob() {
                self.executionEngine.addJob(collection, identifier, {
                    job: job,
                    info: 'SUBSCRIBE',
                });
            }
        });

        return {
            promise: promise,
            dispose: () => {
                callback = () => 0;
                this.executionEngine.addJob(collection, identifier, {
                    job: () => {
                        internalSubscription && internalSubscription.dispose();
                        this.network.decreaseNetworkSubscription(collection, identifier);
                    },
                    info: 'DISPOSE',
                });
            },
        };
    }

    /**
     * Subscribe to updates on a multiple data entity.
     * per collection. Promise for subscription status is a part of return value.
     *
     * Callback is triggered when data entity changes, either via network provider or through local save, saveDiff or remove.
     * Initial entity state is provided on promise resolve (note: callback is not invoked at that time).
     *
     * @param {String} collection Collection entity belongs to
     * @param {Array<String>} identifiers List of entity identifiers
     * @param {Function} callback Callback handler for updates ``function (data) {}``. An update where data
     * is null indicates that the entity has been removed from the repository.
     *
     * @memberof module:modules/repository
     *
     * @function subscribeMultiple
     *
     * @instance
     *
     * @public
     */
    subscribeMultiple(collection, identifiers, callback) {
        this.assertInitialized();

        if (!identifiers) {
            return {
                promise: Promise.resolve(),
                subscriptions: [],
            };
        }

        const childSubscriptions = {};

        const subscriptionKey = identifiers.sort().join('-');

        const promise = new Promise((resolve, reject) => {
            this.validate(
                { name: 'callback', isOk: callback, value: callback },
                { name: 'collection', isOk: collection, value: collection },
                {
                    name: 'identifier',
                    isOk: subscriptionKey !== undefined && subscriptionKey !== null,
                    value: subscriptionKey,
                }
            );

            // Subscribe to internal channel for updates on entity
            identifiers.forEach(id => {
                const escapedName = escapeRegExp(`${collection}_${id}`);
                childSubscriptions[id] = this.channel.subscribe(`^${escapedName}$`, message => {
                    if (message.dataProvider) {
                        message
                            .dataProvider()
                            .then(data => callback(id, data, message.changeset))
                            .catch(err => log.warn(`Callback was not invoked (${err})`));
                    } else {
                        callback(id, null, message.changeset);
                    }
                });
            });

            if (
                this.network.hasProvider(collection) &&
                !this.network.hasNetworkSubscription(collection, subscriptionKey)
            ) {
                const job = this.createMultiJob(
                    collection,
                    subscriptionKey,
                    identifiers,
                    resolve,
                    reject
                );
                this.executionEngine.addJob(collection, subscriptionKey, {
                    job,
                    info: 'SUBSCRIBE_MULTIPLE',
                });
            }
        });

        const subscriptionInfos = Object.keys(childSubscriptions).map(id => ({
            id,
            dispose: () => {
                this.executionEngine.addJob(collection, id, {
                    job: () => {
                        const subscription = childSubscriptions[id];
                        subscription.dispose();
                        this.network.decreaseNetworkSubscription(collection, id);
                    },
                    info: 'DISPOSE_FROM_MULTIPLE',
                });
            },
        }));

        return {
            promise,
            subscriptions: subscriptionInfos,
        };
    }

    createMultiJob(collection, subscriptionKey, identifiers, resolve, reject) {
        return done => {
            const onComplete = (err, dataInfos = []) => {
                if (err) {
                    // if err is NOT_AVAILABLE then subscription is up, but id was not found. updates will come when id exists
                    if (isUnavailable(err)) {
                        resolve(null);
                    } else {
                        reject(err);
                    }
                } else {
                    resolve(dataInfos);
                }
                x.async(done)(); // Notify the queue in the next frame, in order to let promise.then handler to run before launch of the next job
            };
            if (this.network.hasNetworkSubscription(collection, subscriptionKey)) {
                this.network.increaseNetworkSubscription(collection, subscriptionKey);
                onComplete();
                return;
            }

            const provideVersionTag = (cb, itemId) => {
                this.storageManager
                    .getById(collection, itemId)
                    .then(data => cb(data && data.versionTag))
                    .catch(() => cb());
            };

            const onUpdate = (data, diff, itemId, skipPublish, isNotAvailable) => {
                if (diff) {
                    return this.saveDiff(collection, diff, skipPublish);
                } else if (data) {
                    return this.save(collection, data, skipPublish);
                } else if (isNotAvailable) {
                    return this.saveUnavailable(collection, itemId, skipPublish);
                }
                // Neither data nor diff, it means that the item has been removed.
                // Remove throws exception if item is not found, we will catch it.
                return this.remove(collection, itemId)
                    .then(() => null)
                    .catch(() => {});
            };

            this.network.subscribeMultiple(
                collection,
                identifiers,
                provideVersionTag,
                onComplete,
                onUpdate
            );
        };
    }

    /**
     * Wait until job completion.
     *
     * If collection and identifier are provided, a synchronous job will
     * be put on the associated queue, and callback will be called when that
     * job runs.
     *
     * If collection but not id is provided, jobs will be added to all (non empty)
     * queues under the collection 'collection' (Remember, there is one queue instance for
     * each id under a collection). When all the jobs has run, callback will be
     * called.
     *
     * If not collection nor id is provided, jobs will be added to all (non empty) queues,
     * and when all these jobs has run, callback will be called.'
     *
     * @example
     * repository.fetch("events", "1", function () {...});
     * repository.fetch("events", "2", function () {...});
     * repository.fetch("events", "3", function () {...});
     * repository.fetch("markets", "123", function () {...});
     * repository.wait(function () {
     *     // this will run after all the above fetch have finished
     * });
     *
     * @example
     * repository.fetch("events", "1", function () {...});
     * repository.fetch("events", "2", function () {...});
     * repository.fetch("events", "3", function () {...});
     * repository.fetch("markets", "123", function () {...});
     * repository.wait("markets", function () {
     *     // this will run after fetch("markets", ...) has finished.
     *     // the events fetch operations might not have finished
     * });
     *
     * @example
     * repository.fetch("events", "1", function () {...});
     * repository.fetch("events", "2", function () {...});
     * repository.fetch("events", "3", function () {...});
     * repository.fetch("markets", "123", function () {...});
     * repository.wait("events", "2", function () {
     *     // this will run after fetch("events", "2") has finished.
     *     // the other two events fetch operations might not have finished
     * });
     *
     * @memberof module:modules/repository
     *
     * @param {string} collection a collection
     * @param {string} identifier an identifier
     * @param {function} callback a callback
     *
     * @function wait
     *
     * @instance
     *
     * @private
     */
    wait(collection, identifier, callback) {
        this.assertInitialized();
        this.executionEngine.wait(collection, identifier, callback);
    }

    /**
     * An array of objects with collection and identifier is passed to this, and
     * callback is called when all have finished. The collection property is mandatory
     * but the identifier property is optional. If the identifier property is left out,
     * it will wait for all jobs to finished on the specified collection.
     *
     * @example
     * repository.waitForMany([
     *     { collection: "events" },
     *     { collection: "markets", identifier: "123456"}
     * ], callback);
     *
     * // This will wait for all jobs on collection "events" and on "markets" with id=123456
     * // to finish before callback is called.
     *
     * @memberof module:modules/repository
     *
     * @function waitForMany
     * @param {Object[]} pairs pairs
     * @param {function} callback a callback
     *
     * @instance
     *
     * @private
     */
    waitForMany = (pairs, callback) => {
        this.assertInitialized();
        this.executionEngine.waitForMany(pairs, callback);
    };

    /**
     * This will notify (call 'callback') after the Web Socket request queue used by the
     * repository has been idle for at least 'millis' milliseconds.
     *
     * @example
     * repository.waitForIdle(function () {
     *     ...
     * }, 500);
     *
     * @param {Function} callback This function will be called when the queue has been idle between 'millis' and 2*'millis' milliseconds
     * @param {Number} millis For how long the repository must be idle before 'callback' is invoked. Optional, defaults to 0
     *
     * @memberof module:modules/repository
     *
     * @function waitForIdle
     *
     * @instance
     *
     * @public
     */
    waitForIdle = (callback, millis) => {
        this.assertInitialized();
        this.executionEngine.waitForIdle(callback, millis);
    };

    assertInitialized() {
        if (!this.wasInitialized) {
            throw new Error('Init method of the "repository" component was not called.');
        }
    }

    getIdKey(collection) {
        return this.idKeyCollection[collection] || DEFAULT_ID;
    }

    getLocale(collection) {
        return this.localeCollection[collection] || '';
    }

    getId(collection, data) {
        const idKey = this.getIdKey(collection);
        if (!data[idKey]) {
            throw new Error(`could not get id with idKey: ${idKey}`);
        }
        return data[idKey];
    }

    doNetworkFetch(collection, identifier) {
        return new Promise((resolve, reject) =>
            this.network.fetch(collection, identifier, (err, data) =>
                err ? reject(err) : resolve(data)
            )
        );
    }

    doNetworkFetchAndSave(collection, identifier) {
        return this.doNetworkFetch(collection, identifier).then(data =>
            this.save(collection, data)
        );
    }

    validate(...conditions) {
        conditions.forEach(condition => {
            if (!condition.isOk) {
                throw new Error(`Illegal value ${condition.value} for ${condition.name}`);
            }
        });
    }

    getValidationMessage(...conditions) {
        let msg = null;
        try {
            this.validate(...conditions);
        } catch (e) {
            msg = e;
        }
        return msg;
    }

    cloneProvider(obj, collection, identifier) {
        let firstTime = true;

        return () => {
            if (firstTime) {
                firstTime = !firstTime;
                return Promise.resolve(obj);
            }
            return this.storageManager.getById(collection, identifier);
        };
    }
}

// Escapes the RegExp special characters "^", "$", "", ".", "*", "+", "?", "(", ")", "[", "]", "{", "}", and "|" in string.
// Note! This is basically the same as lodash https://lodash.com/docs#escapeRegExp, but we don't have lodash here
// More information: https://stackoverflow.com/a/6969486
function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

function isUnavailable(err) {
    return err === 'NOT_AVAILABLE';
}
